import { useState, useEffect, useContext } from "react";
import { useFormik } from "formik";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Snackbar,
  Fade,
  Slide,
  Chip,
  Paper,
  Avatar,
  Tooltip,
  IconButton,
  Container,
  Stack,
  CardActionArea,
  alpha,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import * as Yup from "yup";
import {
  Download,
  Security,
  Email,
  Smartphone,
  CheckCircle,
  Warning,
  Shield,
  Lock,
  Verified,
  QrCode,
  Refresh,
  ArrowForward,
  ArrowBack,
  Info,
  Close,
  ExpandMore,
  ExpandLess,
  SecurityOutlined,
  PhoneAndroid,
  EmailOutlined,
} from "@mui/icons-material";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import BaseLayout from "layouts/pages/account/components/BaseLayout";
import CustomOTPInput from "components/OTPInput";

// API functions
import {
  generateQRCodeApi,
  verifyQRCodeApi,
  sendVerificationCodeApi,
  verifyCodeApi,
  getUserProfile,
} from "lib/Api/Authentication";

// Context
import { LoginFormContext } from "context/loginFormCtx";
import DraggableDialog from "components/draggagle-dialog";

function AccountTwoStepVerification() {
  const { userDetail, setUserDetail, setNotification } = useContext(LoginFormContext);

  // State management
  const [selectedMethod, setSelectedMethod] = useState(
   null
  );
  const [showQRCode, setShowQRCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [qrData, setQrData] = useState({
    image: "",
    userSecret: "",
  });
  const [showMethodSwitchConfirmation, setShowMethodSwitchConfirmation] = useState(false);
  const [methodToSwitch, setMethodToSwitch] = useState(null);


  // Load user data on component mount`
  useEffect(() => {
    if (userDetail?.mfa_method === 2) {
      setSelectedMethod("totp");
      generateQRCode();
    }else {
      setSelectedMethod("email");
    }
  }, [userDetail]);

  // Fetch user profile to get latest 2FA status
  const fetchUserProfile = async () => {
    try {
      const response = await getUserProfile();
      if (response?.status === 200) {
        setUserDetail(response.data.data);
      }
    } catch (error) {
    }
  };

  // Handle method selection
  const handleMethodChange = (event) => {
    const newMethod = event.target.value;
    const currentMethod =
      userDetail?.mfa_method === 2 ? "totp" : userDetail?.mfa_method === 1 ? "email" : null;

    // Clear OTP field when switching tabs
    setOtp("");

    // If user clicks on the same method that's already active, just select it to show verification
    if (currentMethod === newMethod) {
      setSelectedMethod(newMethod);
      return;
    }

    // Show confirmation dialog for any method switch
    setMethodToSwitch(newMethod);
    setShowMethodSwitchConfirmation(true);
  };

  // Confirm method switch
  const handleConfirmMethodSwitch = async () => {
    setShowMethodSwitchConfirmation(false);
    setSelectedMethod(methodToSwitch);

    // Clear OTP field when confirming method switch
    setOtp("");

    if (methodToSwitch === "totp") {
      await generateQRCode();
    } else if (methodToSwitch === "email") {
      await sendEmailCode();
    }
  };

  // Cancel method switch
  const handleCancelMethodSwitch = () => {
    setMethodToSwitch(null);
    setShowMethodSwitchConfirmation(false);
  };

  // Generate QR code for TOTP
  const generateQRCode = async () => {
    setIsLoading(true);
    try {
      const response = await generateQRCodeApi();
      if (response?.status === 200) {
        const data = response.data.data;
        setQrData({
          image: data.image, // Base64 PNG image
          userSecret: data.userSecret, // Secret key
        });
        setShowQRCode(true);
       
      } 
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  // Handle TOTP verification
  const handleTOTPVerify = async () => {
    if (!qrData.userSecret) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "QR code not generated. Please try again.",
        open: true,
      });
      return;
    }

    setIsVerifying(true);
    try {
      const response = await verifyQRCodeApi({
        otp: otp,
        secret: qrData.userSecret,
      });

      if (response?.status === 200) {
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: "TOTP verification successful!",
          open: true,
        });
        setIsVerified(true);
        setOtp(""); // Clear OTP input after successful verification
        await fetchUserProfile();
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data?.message || "Invalid TOTP code. Please try again.",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Error verifying TOTP code. Please try again.",
        open: true,
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle Email verification
  const handleEmailVerify = async () => {
    setIsVerifying(true);
    try {
      const response = await verifyCodeApi({
        otp: otp,
        email: userDetail?.email,
      });

      if (response?.status === 200) {
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: "Email verification successful!",
          open: true,
        });
        setIsVerified(true);
        setOtp("");
        await fetchUserProfile();
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data?.message || "Invalid OTP code. Please try again.",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Error verifying email code. Please try again.",
        open: true,
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // Send email verification code
  const sendEmailCode = async () => {
    setIsLoading(true);
    try {
      const response = await sendVerificationCodeApi({
        email: userDetail?.email,
      });

      if (response?.status === 200) {
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: "Verification code sent to your email.",
          open: true,
        });
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data?.message || "Failed to send verification code.",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: "Error sending verification code. Please try again.",
        open: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Download QR code
  const downloadQRCode = () => {
    if (qrData.image) {
      // Create a link element to download the base64 image
      const link = document.createElement("a");
      link.download = "2fa-qr-code.png";
      link.href = qrData.image; // Base64 data URL
      link.click();
    }
  };

  // Handle OTP change
  const handleOtpChange = (newValue) => {
    setOtp(newValue);
  };

  // Formik for email verification
  const emailFormik = useFormik({
    initialValues: { otp: "" },
    onSubmit: (values) => {
      handleEmailVerify();
    },
  });

  // Formik for TOTP verification
  const totpFormik = useFormik({
    initialValues: { otp: "" },
    onSubmit: (values) => {
      handleTOTPVerify();
    },
  });

  // Handle OTP change for email
  const handleEmailOtpChange = (newValue) => {
    setOtp(newValue);
    emailFormik.setFieldValue("otp", newValue);
  };

  // Handle OTP change for TOTP
  const handleTotpOtpChange = (newValue) => {
    setOtp(newValue);
    totpFormik.setFieldValue("otp", newValue);
  };

  const steps = ["Choose Method", "Setup Verification", "Verify & Complete"];

  return (
    <Card id="two-factor-auth" sx={{ overflow: "visible" }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Fade in timeout={600}>
          <Box>
            {/* Section Title */}
            <MDBox lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium" mb={1}>
                Two-Factor Authentication
              </MDTypography>
              <MDTypography variant="button" color="text" mb={3} display="block">
                Choose your preferred two-factor authentication method to enhance account security.
              </MDTypography>
            </MDBox>

            {/* Method Selection Radio Buttons */}
            {selectedMethod && (
            <MDBox sx={{ mb: 3 }}>
              <RadioGroup value={selectedMethod} onChange={handleMethodChange} row sx={{ gap: 4 }}>
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                      <Typography variant="h6" fontWeight="600" color="text.primary">
                        Email Verification
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "fit-content", m: 0 }}
                />

                <FormControlLabel
                  value="totp"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                      <Typography variant="h6" fontWeight="600" color="text.primary">
                        Authenticator App
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "fit-content", m: 0 }}
                />
              </RadioGroup>
            </MDBox>
            )}
            {/* Email Verification Section - Only show if email is sent (not when already active) */}
            {userDetail?.mfa_method !== 1 && selectedMethod === "email" && (
              <Fade in timeout={600}>
                <Card
                  sx={{
                    mb: 3,
                  }}
                >
                  <Paper
                    sx={{
                      p: 3,
                      background: "white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                  >
                    <form onSubmit={emailFormik.handleSubmit}>
                      <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
                        Enter Verification Code
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Check your email and enter the 6-digit code below
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mb: 3,
                          p: 2,
                          background: "rgba(25, 118, 210, 0.02)",
                          border: "1px solid rgba(25, 118, 210, 0.1)",
                        }}
                      >
                        <CustomOTPInput 
                          value={otp} 
                          onChange={handleEmailOtpChange} 
                          numInputs={6}
                        />
                      </Box>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        sx={{ justifyContent: "center" }}
                      >
                        <MDButton
                          type="submit"
                          variant="gradient"
                          color="primary" 
                          disabled={isVerifying || otp.length !== 6}
                          startIcon={isVerifying ? <CircularProgress size={16} /> : <Verified />}
                          sx={{
                            minWidth: 140,
                            height: 40,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          {isVerifying ? "Verifying..." : "Verify Code"}
                        </MDButton>
                      </Stack>
                    </form>
                  </Paper>
                </Card>
              </Fade>
            )}

            {/* TOTP Setup Section */}
            {selectedMethod === "totp" && (
              <Fade in timeout={600}>
                <Card
                  sx={{
                    mb: 3,
                    borderRadius: 3,

                    border: "1px solid rgba(25, 118, 210, 0.2)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          mr: 2,
                          width: 40,
                          height: 40,
                          boxShadow: "0 4px 16px rgba(25, 118, 210, 0.3)",
                        }}
                      >
                        <QrCode sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight="600" color="text.primary" gutterBottom>
                          Setup Authenticator App
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Scan the QR code with your authenticator app to get started
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={3}>
                      {/* QR Code Section */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          sx={{
                            p: 3,
                            textAlign: "center",
                            borderRadius: 3,
                            background: "white",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 2,
                            }}
                          >
                            <Typography variant="h6" fontWeight="600" color="text.primary">
                              QR Code
                            </Typography>
                            <Tooltip title="Download QR code for backup">
                              <IconButton
                                onClick={downloadQRCode}
                                disabled={!qrData.image}
                                size="small"
                                sx={{
                                  bgcolor: "rgba(25, 118, 210, 0.1)",
                                  "&:hover": {
                                    bgcolor: "rgba(25, 118, 210, 0.2)",
                                    transform: "scale(1.1)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mb: 2,
                              p: 3,
                              border: "2px dashed",
                              borderColor: "primary.main",
                              borderRadius: 3,
                              backgroundColor: "rgba(25, 118, 210, 0.02)",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {qrData.image ? (
                              <Fade in timeout={800}>
                                <img
                                  src={qrData.image}
                                  alt="QR Code for 2FA Setup"
                                  style={{
                                    width: 200,
                                    height: 200,
                                    borderRadius: 8,
                                    objectFit: "contain",
                                  }}
                                />
                              </Fade>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  py: 4,
                                }}
                              >
                                <CircularProgress
                                  size={40}
                                  sx={{
                                    color: "primary.main",
                                    mb: 1,
                                  }}
                                />
                                <Typography variant="body2" color="text.secondary" fontWeight="500">
                                  Generating QR code...
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          <Paper
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              border: "1px solid rgba(25, 118, 210, 0.1)",
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              For future reference, store the QR code image in a safe place
                            </Typography>
                          </Paper>
                        </Paper>
                      </Grid>

                      {/* Instructions and Verification */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          sx={{
                            p: 3,
                            height: "100%",
                            borderRadius: 3,
                            background: "white",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight="600"
                            color="text.primary"
                            gutterBottom
                          >
                            Setup Instructions
                          </Typography>

                          <List sx={{ mb: 3 }}>
                            <ListItem sx={{ px: 0, py: 1.5 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: "primary.main",
                                    fontSize: "0.875rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  1
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary="Download an authenticator app"
                                secondary="Google Authenticator, Authy, Microsoft Authenticator, or any TOTP-compatible app"
                                primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                                secondaryTypographyProps={{
                                  variant: "body2",
                                  color: "text.secondary",
                                }}
                              />
                            </ListItem>

                            <ListItem sx={{ px: 0, py: 1.5 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: "primary.main",
                                    fontSize: "0.875rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  2
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary="Scan the QR code"
                                secondary="Open your authenticator app and scan the QR code displayed above"
                                primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                                secondaryTypographyProps={{
                                  variant: "body2",
                                  color: "text.secondary",
                                }}
                              />
                            </ListItem>

                            <ListItem sx={{ px: 0, py: 1.5 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: "primary.main",
                                    fontSize: "0.875rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  3
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary="Enter verification code"
                                secondary="Type the 6-digit code from your authenticator app below"
                                primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                                secondaryTypographyProps={{
                                  variant: "body2",
                                  color: "text.secondary",
                                }}
                              />
                            </ListItem>
                          </List>

                          <Divider sx={{ my: 3 }} />
                        </Paper>
                      </Grid>
                    </Grid>
                    {userDetail?.mfa_method !== 2 && (
                      <Paper
                        sx={{
                          p: 3,
                          mt: 3,
                          height: "100%",
                          borderRadius: 3,
                          background: "white",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        }}
                      >
                        <form onSubmit={totpFormik.handleSubmit}>
                          <Typography
                            variant="h6"
                            fontWeight="600"
                            color="text.primary"
                            gutterBottom
                          >
                            Enter Verification Code
                          </Typography>

                          {userDetail?.mfa_method === 2 ? (
                            <>
                              <Typography
                                variant="body1"
                                color="text.primary"
                                sx={{ mb: 1, textAlign: "center", fontWeight: 500 }}
                              >
                                Open your authenticator app to get the 6-digit code.
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 3, textAlign: "center" }}
                              >
                                Please enter the OTP in below box to verify your email.
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 3, textAlign: "center" }}
                            >
                              Enter the 6-digit verification code from your authenticator app
                            </Typography>
                          )}

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mb: 3,
                              p: 2,
                              background: "rgba(25, 118, 210, 0.02)",
                              borderRadius: 2,
                              border: "1px solid rgba(25, 118, 210, 0.1)",
                            }}
                          >
                            <CustomOTPInput 
                              value={otp} 
                              onChange={handleTotpOtpChange} 
                              numInputs={6}
                            />
                          </Box>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            sx={{ justifyContent: "center" }}
                          >
                            <MDButton
                              type="submit"
                              variant="gradient"
                              color="primary"
                              disabled={isVerifying || otp.length !== 6 || isLoading}
                              startIcon={
                                isVerifying ? <CircularProgress size={16} /> : <Verified />
                              }
                              sx={{
                                minWidth: 140,
                                height: 40,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                              }}
                            >
                              {isVerifying ? "Verifying..." : "Verify Code"}
                            </MDButton>
                          </Stack>
                        </form>
                      </Paper>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            )}
          </Box>
        </Fade>
      </Container>

      {/* Unified Method Switch Confirmation Dialog */}
      <DraggableDialog
        dialogTitle={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box>
              <Typography variant="h5" fontWeight="700">
              Switch to {methodToSwitch === "totp" ? "TOTP" : "Email"} Authentication
              </Typography>
            </Box>
          </Box>
        }
        dialogContent={
          <Box sx={{ px: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to switch to {methodToSwitch === "totp" ? "TOTP" : "Email"}{" "}
              verification? This will change your two-factor authentication method.
            </Typography>
          </Box>
        }
        dialogActions={
          <>
            <Button
              onClick={handleCancelMethodSwitch}
              color="secondary"
              sx={{
                minWidth: 120,
                height: 48,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMethodSwitch}
              variant="contained"
              color="primary"
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} />
                ) : methodToSwitch === "totp" ? (
                  <QrCode />
                ) : (
                  <Email />
                )
              }
              sx={{
                minWidth: methodToSwitch === "totp" ? 180 : 120,
                height: 48,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                px: 3,
                boxShadow: "0 8px 32px rgba(25, 118, 210, 0.3)",
              }}
            >
              {isLoading
                ? methodToSwitch === "totp"
                  ? "Generating..."
                  : "Sending..."
                : methodToSwitch === "totp"
                ? "Generate QR Code"
                : "Send Code"}
            </Button>
          </>
        }
        open={showMethodSwitchConfirmation}
        onClose={handleCancelMethodSwitch}
        maxWidth='sm'
        disableBackdropClick
        PaperProps={{
          sx: { borderRadius: 4, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" },
        }}
      />
    </Card>
  );
}

export default AccountTwoStepVerification;
